import type { RejectionCategory } from './eligibilityTypes.js'
import { getRpaVendor, RPA_VENDORS } from './rpaRegistry.js'
import { EYEMED_SETUP_NEEDED_MESSAGE, EYEMED_VITALREV_FIXING_MESSAGE } from './eyemedLogin.js'
import { VERSANT_SETUP_NEEDED_MESSAGE, VERSANT_VITALREV_FIXING_MESSAGE } from './versantLogin.js'

export type TerminalState = 'eligible' | 'ineligible' | 'needs_review' | 'error'

export type TerminalReason =
  | 'coverage_active'
  | 'coverage_inactive'
  | 'eyemed_all_services_no'
  | 'member_not_found'
  | 'coverage_terminated'
  | 'not_enrolled_rpa'
  | 'not_in_network'
  | 'payer_unknown_response'
  | 'invalid_dob'
  | 'subscriber_mismatch'
  | 'rejection_other'
  | 'stedi_http_error'
  | 'network_error'
  | 'transform_error'
  | 'rpa_bot_error'
  | 'rpa_login_failed'
  | 'rpa_mfa_setup_needed'
  | 'rpa_mfa_paused'
  | 'rpa_missing_input'
  | 'rpa_provider_not_found'
  | 'rpa_parse_failure'
  | 'rpa_degraded_data'
  | 'rpa_retries_exhausted'
  | 'rpa_not_retryable'
  | 'rpa_unreached'
  | 'rpa_routing_blocked'
  | 'rpa_terminal_uipath'
  | 'unknown'

/**
 * The phrase the eligibility sweep's clock fallback puts in the doc's `error`
 * for a vision check that never ran (it crashed between its creation and the
 * queue write, so no lane ever picked it up). The classifier keys
 * `rpa_unreached` on it, ahead of the generic `not_retryable` outcome the
 * sweep also stamps, so the slideout says "never ran, re-run" instead of the
 * integration-incomplete copy that outcome carries for bot-era docs.
 */
export const CHECK_UNREACHED_PHRASE = 'never reached the scraper lane'

export interface TerminalClassification {
  terminalState: TerminalState
  terminalReason: TerminalReason
}

export interface TerminalClassifierInput {
  status?: 'pending' | 'success' | 'error' | 'timeout'
  outcome?: string
  success?: boolean
  resultStatus?: 'active' | 'inactive' | 'unknown' | 'lookup_error'
  rejectionCategory?: RejectionCategory
  type?: string
  lastError?: string | null
  error?: string | null
  retryCount?: number
  retriesExhausted?: boolean
  /** The ingest's stamp on an EyeMed success whose eligibility table listed
   *  services and read No for every one (`eyemedAllServicesNo` in
   *  eligibilityTypes.ts): a found member with nothing left this period. */
  eyemedAllServicesNo?: boolean
}

export const INELIGIBLE_PATTERNS: ReadonlyArray<{ regex: RegExp; reason: TerminalReason }> = [
  { regex: /\bcoverage\s+terminat\w*\b/i, reason: 'coverage_terminated' },
  { regex: /\bplan\s+terminat\w*\b/i, reason: 'coverage_terminated' },
  { regex: /\bcoverage\s+expir\w*\b/i, reason: 'coverage_terminated' },
  { regex: /\bplan\s+inactive\b/i, reason: 'coverage_terminated' },
  { regex: /\b(patient|member|subscriber)\s+not\s+found\b/i, reason: 'member_not_found' },
  { regex: /\bnot\s+found\s+on\s+(plan|vsp|eyemed|file)\b/i, reason: 'member_not_found' },
  { regex: /\bnot\s+enrolled\b/i, reason: 'not_enrolled_rpa' },
  { regex: /\bno\s+(record|coverage|policy)\b/i, reason: 'not_enrolled_rpa' },
  { regex: /\b(ineligible|not\s+covered|not\s+on\s+plan)\b/i, reason: 'not_enrolled_rpa' },
]

function classifyRpaReason(text: string): TerminalReason | null {
  for (const { regex, reason } of INELIGIBLE_PATTERNS) {
    if (regex.test(text)) return reason
  }
  return null
}

export function stateFromReason(reason: TerminalReason): TerminalState {
  switch (reason) {
    case 'coverage_active':
      return 'eligible'
    case 'coverage_inactive':
    case 'coverage_terminated':
    case 'not_enrolled_rpa':
    case 'eyemed_all_services_no':
      return 'ineligible'
    // member_not_found is an identity-match failure (AAA 72/75 — typically a
    // wrong/missing member ID), not a coverage verdict; recovery routes it to
    // patient_correction, so it belongs with the correctable rejections.
    case 'member_not_found':
    case 'invalid_dob':
    case 'subscriber_mismatch':
    case 'rejection_other':
    case 'payer_unknown_response':
    case 'rpa_retries_exhausted':
    case 'rpa_not_retryable':
    case 'rpa_unreached':
    case 'rpa_missing_input':
    case 'rpa_provider_not_found':
    case 'not_in_network':
      return 'needs_review'
    // rpa_routing_blocked (the vendor kill-switch park) is a VitalRev-side
    // pause staff can't act on, so it reads as a system state — unlike
    // rpa_not_retryable above (the auto_retry promotion for docs that can
    // never be re-dispatched), which a human has to pick up like
    // retries-exhausted. The two rpa_mfa reasons are the EyeMed login not
    // set up or paused: a state of the login, never a verdict on the patient.
    case 'stedi_http_error':
    case 'network_error':
    case 'transform_error':
    case 'rpa_bot_error':
    case 'rpa_login_failed':
    case 'rpa_mfa_setup_needed':
    case 'rpa_mfa_paused':
    case 'rpa_parse_failure':
    case 'rpa_degraded_data':
    case 'rpa_terminal_uipath':
    case 'rpa_routing_blocked':
    case 'unknown':
      return 'error'
  }
}

function pack(reason: TerminalReason): TerminalClassification {
  return { terminalState: stateFromReason(reason), terminalReason: reason }
}

function classifyRpaError(input: TerminalClassifierInput): TerminalClassification {
  const text = (input.lastError ?? input.error ?? '').toString()
  // Ahead of the patterns below, which only guess from prose: VSP's wording
  // ("does not participate in this patient's plan") is one rewording away from
  // tripping `not on plan` and calling the PATIENT not enrolled.
  if (text.toLowerCase().includes('not_in_network')) return pack('not_in_network')
  const ineligibleReason = classifyRpaReason(text)
  if (ineligibleReason) return pack(ineligibleReason)
  // Administrative outcomes stamped by our own writers beat every text bucket
  // below: those writers deliberately preserve the ORIGINAL failure in
  // `lastError`, so the text says why the check failed back then, not why it
  // is parked for staff now. A definitive ineligible keyword above still wins
  // — the same precedence `retriesExhausted` follows.
  //
  // A check that never ran is told apart first, by the phrase the sweep's
  // clock fallback puts in `error` for a vision doc with no queue stamp. Read
  // from `error` as well as the preserved `lastError`: the sweep keeps an
  // earlier failure text in `lastError` when the doc carried one.
  //
  // The gcp text buckets that follow read a doc population these can't
  // collide with: `routing_blocked` is the kill-switch park's; `not_retryable`
  // is written only by that clock fallback (and stands on bot-era docs the
  // retired engine's `promoteUnretryable` parked), neither of which a lane
  // execution ever produced, while `parkQueuedGcpChecks` and the worker ingest
  // write no `outcome` at all.
  const own = (input.error ?? '').toString().toLowerCase()
  if (own.includes(CHECK_UNREACHED_PHRASE) || text.toLowerCase().includes(CHECK_UNREACHED_PHRASE)) {
    return pack('rpa_unreached')
  }
  if (input.outcome === 'routing_blocked') return pack('rpa_routing_blocked')
  if (input.outcome === 'not_retryable') return pack('rpa_not_retryable')
  // The worker's EyeMed code-screen endings: the login is not set up, or is
  // paused (its own message says whose next step it is). Ahead of
  // login_failed — these are not wrong credentials.
  if (text.toLowerCase().includes('mfa_setup_needed')) return pack('rpa_mfa_setup_needed')
  if (text.toLowerCase().includes('mfa_paused')) return pack('rpa_mfa_paused')
  // The gcp scrape worker's taxonomy prefix (underscore form only, so bot
  // prose like "Login failed" on UiPath docs never lands here): credentials
  // missing/unreadable or the portal rejecting them — a setup problem, not a
  // patient one, so it gets its own copy instead of the generic bot error.
  if (text.toLowerCase().includes('login_failed')) return pack('rpa_login_failed')
  // The worker's provider_not_found code (the check's provider is not in the
  // portal's provider list): a clinic-data problem staff fix, with its own copy.
  if (text.toLowerCase().includes('provider_not_found')) return pack('rpa_provider_not_found')
  // The check-lane drain's missing-data park wording (checkLaneDispatch.service.ts).
  if (text.toLowerCase().includes('missing patient name or date of birth')) {
    return pack('rpa_missing_input')
  }
  if (input.retriesExhausted) return pack('rpa_retries_exhausted')
  const lower = text.toLowerCase()
  if (lower.includes('parse failed') || lower.includes('could not be parsed')) return pack('rpa_parse_failure')
  if (lower.includes('empty benefits')) return pack('rpa_degraded_data')
  if (lower.includes('manual stop') || lower.includes('terminating') || lower.includes('webhook never arrived')) return pack('rpa_terminal_uipath')
  return pack('rpa_bot_error')
}

function isRpaType(type: string | undefined): boolean {
  return type !== undefined && type !== 'stedi'
}

export function classifyTerminal(
  input: TerminalClassifierInput,
): TerminalClassification | null {
  if (input.status === 'pending') return null
  if (input.status === 'timeout' && !input.retriesExhausted) return null

  if (isRpaType(input.type)) {
    if (input.status === 'success' && input.success === true) {
      // A success that found benefits, every service of which reads No: the
      // ingest's stamp, so the verdict is decided once and read everywhere.
      // Only a success carries it — on any other doc the stamp says nothing.
      if (input.eyemedAllServicesNo === true) return pack('eyemed_all_services_no')
      return pack('coverage_active')
    }
    return classifyRpaError(input)
  }

  // Stedi branch (type === 'stedi', or legacy docs with no type)
  if (input.outcome === 'success') {
    switch (input.resultStatus) {
      case 'active': return pack('coverage_active')
      case 'inactive': return pack('coverage_inactive')
      case 'unknown': return pack('payer_unknown_response')
    }
  }

  if (input.outcome === 'lookup_error') {
    switch (input.rejectionCategory) {
      case 'member_not_found': return pack('member_not_found')
      case 'coverage_terminated': return pack('coverage_terminated')
      case 'invalid_dob': return pack('invalid_dob')
      case 'no_match_or_invalid_subscriber': return pack('subscriber_mismatch')
      case 'other':
      case undefined:
      default: return pack('rejection_other')
    }
  }

  if (input.outcome === 'stedi_error') return pack('stedi_http_error')
  if (input.outcome === 'network_error') return pack('network_error')
  if (input.outcome === 'transform_error') return pack('transform_error')

  return pack('unknown')
}

/** The login-failed line always names the portal whose credentials to fix. */
function loginFailedCopy(portal: string): string {
  return `The ${portal} integration is not set up for this clinic or its credentials are wrong. Update the ${portal} credentials and re-run the check.`
}

/** The provider-not-found line names the portal whose provider list was searched. */
function providerNotFoundCopy(portal: string): string {
  return `The provider on this check isn't listed on the ${portal} portal account. Check the doctor on this appointment or the clinic's default provider, then re-run the check.`
}

/** "VSP, EyeMed or Versant" — the static map's entry when no check type is
 *  at hand; `reasonCopyFor` narrows it to the check's own portal. */
const RPA_PORTAL_NAMES = ((names: string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names.join(''))(
  RPA_VENDORS.map((v) => v.displayName),
)

export const REASON_COPY: Record<TerminalReason, string> = {
  coverage_active: 'Coverage confirmed by the payer.',
  coverage_inactive: 'Payer responded but the coverage is inactive on the date of service.',
  eyemed_all_services_no: 'EyeMed shows every service as No.',
  member_not_found: "Payer doesn't have a record of this patient on this plan.",
  coverage_terminated: "The patient's coverage on this plan has been terminated.",
  not_enrolled_rpa: 'Bot confirmed the patient is not enrolled in the plan.',
  not_in_network: 'Practice not in network',
  payer_unknown_response: "Payer responded but didn't give a clear answer. Re-check or call the payer.",
  invalid_dob: "Payer rejected the request — date of birth doesn't match their records.",
  subscriber_mismatch: "Payer couldn't match the subscriber. Verify member ID and insurance card.",
  rejection_other: 'Payer rejected the request with an unspecified reason.',
  stedi_http_error: 'The eligibility service returned an error.',
  network_error: "Couldn't reach the eligibility service.",
  transform_error: "We received the response but couldn't parse it. Engineering has been notified.",
  rpa_bot_error: "The bot couldn't complete the check. Try a manual re-check if it doesn't clear.",
  rpa_login_failed: loginFailedCopy(RPA_PORTAL_NAMES),
  rpa_mfa_setup_needed: EYEMED_SETUP_NEEDED_MESSAGE,
  rpa_mfa_paused: EYEMED_VITALREV_FIXING_MESSAGE,
  rpa_missing_input: "The check is missing the patient's name or date of birth. Update the patient details and re-run the check.",
  rpa_provider_not_found: providerNotFoundCopy(RPA_PORTAL_NAMES),
  rpa_parse_failure: "We received a bot payload but couldn't read the benefits. Engineering has been notified.",
  rpa_degraded_data: 'The bot returned empty benefits. Likely an upstream site change — engineering has been notified.',
  rpa_retries_exhausted: "The check failed repeatedly with no answer from the payer's portal. Try a manual check or call the payer.",
  rpa_not_retryable: "This check can't be retried automatically — the clinic's integration or the check record is incomplete. Verify with the payer directly or contact VitalRev AI support.",
  rpa_unreached: 'The automated check never started, so there is no result. Re-run the check.',
  rpa_routing_blocked: "Automated checks for this payer are temporarily paused by VitalRev. A manual re-check won't go through until the pause is lifted.",
  rpa_terminal_uipath: 'The automated check stopped before producing a result. Engineering has been notified.',
  unknown: "Couldn't classify this failure. Engineering has been notified.",
}

/** The reasons whose copy names a portal — narrowed to the check's own by
 *  `reasonCopyFor`; the static map carries the "VSP, EyeMed or Versant" form. */
const PORTAL_COPY: Partial<Record<TerminalReason, (portal: string) => string>> = {
  rpa_login_failed: loginFailedCopy,
  rpa_provider_not_found: providerNotFoundCopy,
  // A paused code login names its own portal; EyeMed's words stay for every
  // other portal, as before Versant.
  rpa_mfa_setup_needed: (portal) => portal === 'Versant' ? VERSANT_SETUP_NEEDED_MESSAGE : EYEMED_SETUP_NEEDED_MESSAGE,
  rpa_mfa_paused: (portal) => portal === 'Versant' ? VERSANT_VITALREV_FIXING_MESSAGE : EYEMED_VITALREV_FIXING_MESSAGE,
  // Versant's search is by name and date of birth, so its not-found says so.
  member_not_found: (portal) => portal === 'Versant'
    ? 'No patient in Versant with this name and date of birth.'
    : REASON_COPY.member_not_found,
}

/**
 * The reason line a surface shows for a check. Same as `REASON_COPY[reason]`
 * except for the portal-naming reasons (`PORTAL_COPY`), where the portal is
 * named from the check's `type` — a login failure or a missing provider is
 * per vendor (VSP and EyeMed are separate accounts), and the login copy used
 * to say "VSP" for an EyeMed check too. Those reasons only ever arise on a
 * vsp/eyemed/versant-typed doc, so the static fallback is for a type the
 * registry doesn't know.
 */
export function reasonCopyFor(reason: TerminalReason, checkType?: string): string {
  const build = PORTAL_COPY[reason]
  if (!build) return REASON_COPY[reason]
  const vendor = getRpaVendor(checkType)?.displayName
  return vendor ? build(vendor) : REASON_COPY[reason]
}
